package wat.ui

import kotlinx.browser.document
import org.w3c.dom.*

// Lightweight DOM helpers for building UI
fun el(tag: String, init: HTMLElement.() -> kotlin.Unit = {}): HTMLElement {
    val element = document.createElement(tag) as HTMLElement
    element.init()
    return element
}

fun div(init: HTMLDivElement.() -> kotlin.Unit = {}): HTMLDivElement {
    val element = document.createElement("div") as HTMLDivElement
    element.init()
    return element
}

fun span(text: String, init: HTMLSpanElement.() -> kotlin.Unit = {}): HTMLSpanElement {
    val element = document.createElement("span") as HTMLSpanElement
    element.textContent = text
    element.init()
    return element
}

fun button(text: String, init: HTMLButtonElement.() -> kotlin.Unit = {}): HTMLButtonElement {
    val element = document.createElement("button") as HTMLButtonElement
    element.textContent = text
    element.init()
    return element
}

fun input(type: String = "text", init: HTMLInputElement.() -> kotlin.Unit = {}): HTMLInputElement {
    val element = document.createElement("input") as HTMLInputElement
    element.type = type
    element.init()
    return element
}

fun textarea(init: HTMLTextAreaElement.() -> kotlin.Unit = {}): HTMLTextAreaElement {
    val element = document.createElement("textarea") as HTMLTextAreaElement
    element.init()
    return element
}

fun select(init: HTMLSelectElement.() -> kotlin.Unit = {}): HTMLSelectElement {
    val element = document.createElement("select") as HTMLSelectElement
    element.init()
    return element
}

fun option(text: String, value: String, init: HTMLOptionElement.() -> kotlin.Unit = {}): HTMLOptionElement {
    val element = document.createElement("option") as HTMLOptionElement
    element.textContent = text
    element.value = value
    element.init()
    return element
}

fun label(text: String, init: HTMLLabelElement.() -> kotlin.Unit = {}): HTMLLabelElement {
    val element = document.createElement("label") as HTMLLabelElement
    element.textContent = text
    element.init()
    return element
}

fun svg(viewBox: String, width: String, height: String, init: Element.() -> kotlin.Unit = {}): Element {
    val element = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    element.setAttribute("viewBox", viewBox)
    element.setAttribute("width", width)
    element.setAttribute("height", height)
    element.init()
    return element
}

fun svgEl(tag: String, attrs: Map<String, String> = emptyMap()): Element {
    val element = document.createElementNS("http://www.w3.org/2000/svg", tag)
    attrs.forEach { (k, v) -> element.setAttribute(k, v) }
    return element
}

fun HTMLElement.css(vararg styles: Pair<String, String>) {
    styles.forEach { (k, v) -> style.setProperty(k, v) }
}

fun HTMLElement.appendAll(vararg elements: Element) {
    elements.forEach { appendChild(it) }
}

fun HTMLElement.clear() {
    innerHTML = ""
}

// Theme system
enum class Theme { DARK, LIGHT }

object ThemeManager {
    var current: Theme = Theme.DARK
        private set

    fun init() {
        val saved = try { kotlinx.browser.window.localStorage.getItem("wat_theme") } catch (_: Exception) { null }
        current = if (saved == "light") Theme.LIGHT else Theme.DARK
        applyToDocument()
    }

    fun toggle() {
        current = if (current == Theme.DARK) Theme.LIGHT else Theme.DARK
        try { kotlinx.browser.window.localStorage.setItem("wat_theme", if (current == Theme.LIGHT) "light" else "dark") } catch (_: Exception) {}
        applyToDocument()
    }

    fun isDark() = current == Theme.DARK

    private fun applyToDocument() {
        val body = document.body ?: return
        val html = document.documentElement ?: return
        if (isDark()) {
            body.className = "dark"
            html.className = "dark"
            body.style.background = "#111118"
            body.style.color = "#fff"
            document.querySelector("meta[name=theme-color]")?.setAttribute("content", "#111118")
        } else {
            body.className = "light"
            html.className = "light"
            body.style.background = "#ffffff"
            body.style.color = "#1a1a2e"
            document.querySelector("meta[name=theme-color]")?.setAttribute("content", "#ffffff")
        }
    }
}

// Common styles as CSS strings — theme-aware
object Styles {
    const val A1_RAW = "#2dd4bf"
    val A1 get() = if (ThemeManager.isDark()) "#2dd4bf" else "#0d9485"
    val A2 get() = if (ThemeManager.isDark()) "#14b8a6" else "#0a8a7a"
    const val INDIGO = "#818cf8"
    const val PURPLE = "#c084fc"

    val BG get() = if (ThemeManager.isDark()) "#111118" else "#ffffff"
    val BG_DARK get() = if (ThemeManager.isDark()) "#0d0d14" else "#f0f0f5"
    val TEXT get() = if (ThemeManager.isDark()) "#fff" else "#1a1a2e"
    val TEXT_SEC get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.5)" else "rgba(0,0,0,0.55)"
    val TEXT_TER get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.25)" else "rgba(0,0,0,0.35)"
    val TEXT_QUA get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.15)" else "rgba(0,0,0,0.2)"
    val CARD_BG get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.08)" else "#f5f5fa"
    val CARD_BORDER get() = if (ThemeManager.isDark()) "#3a3a4a" else "#c8c8d0"
    val INPUT_BG get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.07)" else "rgba(0,0,0,0.06)"
    val INPUT_BORDER get() = if (ThemeManager.isDark()) "#3a3a4a" else "#c8c8d0"
    val NAV_BG get() = if (ThemeManager.isDark()) "rgba(10,10,16,0.98)" else "rgba(255,255,255,0.96)"
    val NAV_BORDER get() = if (ThemeManager.isDark()) "#3a3a4a" else "#c8c8d0"

    // Extra helpers for inline styles
    val TEXT_70 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.7)" else "rgba(0,0,0,0.75)"
    val TEXT_60 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.6)" else "rgba(0,0,0,0.6)"
    val TEXT_45 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.45)" else "rgba(0,0,0,0.5)"
    val TEXT_40 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.4)" else "rgba(0,0,0,0.45)"
    val TEXT_35 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.35)" else "rgba(0,0,0,0.4)"
    val TEXT_30 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.3)" else "rgba(0,0,0,0.38)"
    val TEXT_20 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.2)" else "rgba(0,0,0,0.28)"
    val TEXT_08 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.08)" else "rgba(0,0,0,0.08)"
    val TEXT_06 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.06)" else "rgba(0,0,0,0.06)"
    val TEXT_04 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.04)" else "rgba(0,0,0,0.05)"
    val TEXT_05 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.05)" else "rgba(0,0,0,0.06)"
    val TEXT_10 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.1)" else "rgba(0,0,0,0.1)"
    val TEXT_15 get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.15)" else "rgba(0,0,0,0.15)"
    val SEPARATOR get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.04)" else "rgba(0,0,0,0.08)"
    val BG_GRAD get() = if (ThemeManager.isDark()) "linear-gradient(180deg,#111118 0%,#191926 100%)" else "linear-gradient(180deg,#ffffff 0%,#f5f5fa 100%)"
    val DROPDOWN_BG get() = if (ThemeManager.isDark()) "rgba(17,17,24,0.98)" else "rgba(255,255,255,0.98)"
    val DROPDOWN_SHADOW get() = if (ThemeManager.isDark()) "0 8px 32px rgba(0,0,0,0.5)" else "0 8px 32px rgba(0,0,0,0.12)"
    val COLOR_SCHEME get() = if (ThemeManager.isDark()) "dark" else "light"
    val INP_STYLE get() = "background:${INPUT_BG};border:1.5px solid ${INPUT_BORDER};border-radius:10px;padding:10px 12px;color:$TEXT;font-size:16px;font-family:inherit;outline:none;width:100%;box-sizing:border-box;font-weight:700"
    val LBL_STYLE get() = "font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:${TEXT_30};margin-bottom:6px"

    /**
     * Adapt color for light theme readability.
     * On dark theme returns color as-is.
     * On light theme: darkens based on perceived luminance — bright colors
     * (yellow, lime, cyan) get darkened much more than already-dark colors.
     */
    fun adaptColor(hex: String): String {
        if (ThemeManager.isDark()) return hex
        if (!hex.startsWith("#") || hex.length < 7) return hex
        try {
            val r = hex.substring(1, 3).toInt(16)
            val g = hex.substring(3, 5).toInt(16)
            val b = hex.substring(5, 7).toInt(16)
            // Perceived luminance (0-1), human eye weights
            val lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255.0
            // Very aggressive darkening for light theme readability
            val factor = when {
                lum > 0.7  -> 0.60  // very bright (yellow, lime, light cyan) → 60%
                lum > 0.55 -> 0.52  // bright (orange, green, teal) → 52%
                lum > 0.45 -> 0.42  // medium-bright (pink, cyan) → 42%
                lum > 0.35 -> 0.32  // medium → 32%
                else       -> 0.18  // already dark (purple, deep blue) → 18%
            }
            val f = 1.0 - factor
            val nr = (r * f).toInt().coerceIn(0, 255)
            val ng = (g * f).toInt().coerceIn(0, 255)
            val nb = (b * f).toInt().coerceIn(0, 255)
            return "#${nr.toString(16).padStart(2,'0')}${ng.toString(16).padStart(2,'0')}${nb.toString(16).padStart(2,'0')}"
        } catch (_: Exception) { return hex }
    }

    /** Chart grid line opacity — stronger on light */
    val GRID_LINE get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.06)" else "rgba(0,0,0,0.1)"
    val GRID_LINE_DASH get() = if (ThemeManager.isDark()) "rgba(255,255,255,0.05)" else "rgba(0,0,0,0.08)"

    /** Common accent colors — adapted for light theme */
    val YELLOW get() = if (ThemeManager.isDark()) "#fbbf24" else "#946800"
    val PINK get() = if (ThemeManager.isDark()) "#fb7185" else "#dc3055"
    val INDIGO_A get() = if (ThemeManager.isDark()) "#818cf8" else "#4a52c4"

    val CARD get() = if (ThemeManager.isDark()) {
        "background:$CARD_BG;border:1.5px solid $CARD_BORDER;border-radius:16px;padding:clamp(12px,2.5vw,20px);margin-bottom:28px"
    } else {
        "background:#f5f5fa;border:1.5px solid $CARD_BORDER;border-radius:16px;padding:clamp(12px,2.5vw,20px);margin-bottom:28px"
    }
    val INPUT get() = "background:$INPUT_BG;border:1.5px solid $INPUT_BORDER;border-radius:14px;padding:12px 14px;color:$TEXT;font-size:14px;font-family:inherit;outline:none;width:100%;box-sizing:border-box;font-weight:500"
    val BTN_PRIMARY get() = if (ThemeManager.isDark()) {
        "padding:15px;border-radius:14px;border:none;background:linear-gradient(135deg,$A1_RAW,#6366f1);color:#fff;font-size:16px;font-weight:800;cursor:pointer;letter-spacing:1px;text-transform:uppercase;box-shadow:0 6px 30px ${A1_RAW}40;width:100%"
    } else {
        "padding:15px;border-radius:14px;border:none;background:#3a3a4a;color:#fff;font-size:16px;font-weight:800;cursor:pointer;letter-spacing:1px;text-transform:uppercase;box-shadow:0 2px 8px rgba(0,0,0,0.08);width:100%"
    }

    /** Action button — save, submit */
    val BTN_ACTION get() = if (ThemeManager.isDark()) {
        "background:#4f46e5;color:#fff;box-shadow:0 4px 20px #4f46e540"
    } else {
        "background:#3a3a4a;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.08)"
    }

    /** Secondary action — diary save etc */
    val BTN_ACTION2 get() = if (ThemeManager.isDark()) {
        "background:#38bdf8;color:#fff"
    } else {
        "background:#3a3a4a;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.08)"
    }
    val BTN_SECONDARY get() = if (ThemeManager.isDark()) {
        "padding:13px 20px;border-radius:14px;border:1.5px solid $CARD_BORDER;background:rgba(255,255,255,0.04);color:$TEXT_SEC;font-size:15px;font-weight:600;cursor:pointer"
    } else {
        "padding:13px 20px;border-radius:14px;border:1.5px solid $CARD_BORDER;background:#f5f5fa;color:$TEXT_SEC;font-size:15px;font-weight:600;cursor:pointer"
    }
    val LABEL get() = "font-size:12px;color:#2dd4bf;font-weight:700;display:block;margin-bottom:6px;text-transform:uppercase;letter-spacing:1px"
}

// Logo: TRACTOR gradient — use everywhere
val LOGO_STYLE get() = if (ThemeManager.isDark()) {
    "font-family:'Space Grotesk',system-ui;font-weight:900;letter-spacing:3px;line-height:1;background:linear-gradient(135deg,${Styles.A1_RAW},#818cf8,#c084fc);-webkit-background-clip:text;-webkit-text-fill-color:transparent"
} else {
    "font-family:'Space Grotesk',system-ui;font-weight:900;letter-spacing:3px;line-height:1;background:linear-gradient(135deg,#0d9485,#4a52c4,#8b5cf6);-webkit-background-clip:text;-webkit-text-fill-color:transparent"
}
